import React, { useEffect, useState } from 'react';

import { Checkbox, Position } from '@blueprintjs/core';
import Flex from '@components/Flex';
import PlatformFilterButton from '@components/PlatformFilterButton';
import PlatformListFilter from '@components/PlatformListFilter';

import { DesignerFilterOption } from '../../utils';

export interface DesignersFilterProps {
    // Every designer holding work on this Request, already ordered
    options: DesignerFilterOption[];

    // Applied selection; ids, and an empty list means "no filter"
    selectedDesignerIds: ID[];

    onApply: (designerIds: ID[]) => void;
}

export type Props = DesignersFilterProps;

// Order-insensitive, so re-checking the same people in a different order is not an "apply"
const selectionKey = (designerIds: ID[]) => [...designerIds].sort().join(',');

// Assigned-designer filter over the phases of one Request.
//
// The draft lives here and only reaches the page on apply, which is what makes ticking three
// people one re-filter rather than three. The applied selection is mirrored back into the
// draft, so a page-level reset — a new Request, a teardown — visibly clears the popover
// instead of leaving ghost ticks in a filter that is no longer doing anything.
const DesignersFilter: React.FC<Props> = ({ options, selectedDesignerIds, onApply }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [draftDesignerIds, setDraftDesignerIds] = useState<ID[]>(selectedDesignerIds);

    useEffect(() => {
        setDraftDesignerIds(selectedDesignerIds);
    }, [selectionKey(selectedDesignerIds)]);

    const getFilterTagLabel = () => {
        if (draftDesignerIds.length === 0) {
            return '';
        }

        // Named while the list is short enough to read at a glance, counted after that: a chip
        // that grows without bound pushes the rest of the filter row off screen
        const selectedOptions = options.filter((option) => draftDesignerIds.includes(option.id));
        if (selectedOptions.length === 0) {
            return `${draftDesignerIds.length} selected`;
        }

        if (selectedOptions.length <= 2) {
            return selectedOptions.map((option) => option.fullName).join(', ');
        }

        return `${selectedOptions.length} selected`;
    };

    const getFilterTagTitle = () => (draftDesignerIds.length === 0 ? 'Designers' : 'Designers:');

    const handleToggle = (designerId: ID) => {
        setDraftDesignerIds(
            draftDesignerIds.includes(designerId)
                ? draftDesignerIds.filter((id) => id !== designerId)
                : [...draftDesignerIds, designerId]
        );
    };

    const handleRemoveFilter = () => {
        setDraftDesignerIds([]);

        if (selectedDesignerIds.length !== 0) {
            onApply([]);
        }

        setIsOpen(false);
    };

    const handleApplyFilter = () => {
        if (selectionKey(draftDesignerIds) !== selectionKey(selectedDesignerIds)) {
            onApply(draftDesignerIds);
        }
    };

    const renderRow = (option: DesignerFilterOption) => (
        <li className="mb-1" key={option.id}>
            <Flex align="center" justify="space-between">
                <Checkbox
                    className="mb-0"
                    checked={draftDesignerIds.includes(option.id)}
                    label={option.fullName}
                    onChange={() => handleToggle(option.id)}
                />
            </Flex>
        </li>
    );

    return (
        <PlatformListFilter<DesignerFilterOption>
            isOpen={isOpen}
            removable={draftDesignerIds.length > 0}
            title="Designers"
            position={Position.BOTTOM_LEFT}
            minWidth={260}
            maxListHeight={320}
            list={options}
            itemRenderer={renderRow}
            onApply={handleApplyFilter}
            onClose={() => setIsOpen(false)}
            onRemove={handleRemoveFilter}
        >
            <PlatformFilterButton
                icon="people"
                onClick={() => setIsOpen(!isOpen)}
                label={getFilterTagLabel()}
                title={getFilterTagTitle()}
            />
        </PlatformListFilter>
    );
};

export default DesignersFilter;
